/**
 * Tracked changes as the open editor sees them, in the shape the review rows
 * already paint (`DocxChange`, which is also what the server lists).
 *
 * Ids line up on purpose: both the engine (`docx_engine/src/tracked.js`) and
 * the editor key a change by its `w:id`, so a row from an agent's `review`
 * payload can be accepted in the page, and a change accepted on the server is
 * gone from the live list on the next reload.
 */
import type { DocxChange, DocxChangeKind } from "../../api/types/draft";
import type { DocxTrackedChange } from "../draftEditor/surface";

const KIND: Record<DocxTrackedChange["type"], DocxChangeKind> = {
  insertion: "insert",
  deletion: "delete",
  replacement: "replace",
  paragraphMarkInsertion: "insertParagraphMark",
  paragraphMarkDeletion: "deleteParagraphMark",
  paragraphPropertiesChanged: "paragraphFormatting",
  rowInserted: "insertRow",
  rowDeleted: "deleteRow",
  rowPropertiesChanged: "tableFormatting",
  cellInserted: "insertRow",
  cellDeleted: "deleteRow",
  cellMerged: "tableFormatting",
  cellPropertiesChanged: "tableFormatting",
  tableInserted: "insertRow",
  tableDeleted: "deleteRow",
  tablePropertiesChanged: "tableFormatting"
};

const TEXT_KINDS = new Set<DocxChangeKind>(["insert", "delete", "replace"]);

export const toDocxChange = (change: DocxTrackedChange): DocxChange => {
  const kind = KIND[change.type] ?? change.type;
  const ids = [String(change.id), ...change.relatedIds.map(String)];
  const base: DocxChange = {
    id: String(change.id),
    ids,
    kind,
    author: change.author,
    date: change.date,
    story: "body"
  };
  if (TEXT_KINDS.has(kind)) {
    return {
      ...base,
      before:
        kind === "insert"
          ? ""
          : change.deletedText ?? (kind === "delete" ? change.text : ""),
      after: kind === "delete" ? "" : change.text,
      text: change.text || change.deletedText
    };
  }
  return { ...base, text: change.text, description: undefined };
};

/** Every `w:id` a decision on these rows must cover. */
export const revisionIdsOf = (changes: readonly DocxChange[]): number[] => {
  const ids = new Set<number>();
  for (const change of changes) {
    for (const id of change.ids ?? [change.id]) {
      const n = Number(id);
      if (Number.isInteger(n)) ids.add(n);
    }
  }
  return [...ids];
};
